import { CubicBezier } from "../../../d3-mapp/curve/bezier.js";
import { splitBezier } from "../../../d3-mapp/curve/split-bezier.js";
export const UNTAGGED_TAG = "_untagged_";
export const UNDEFINED_TAG = "_undefined_";
/**
 * Convenience method to get the tag for a split-off PathSection. When the
 * section has not been tagged, UNTAGGED_TAG is returned. When PathSection
 * is empty then UNDEFINED_TAG gets returned.
 * @param pathSection The potentially tagged path section
 * @returns The tag that applies to given pathSection
 */
export const getTag = (pathSection) => {
    if (pathSection.section &&
        pathSection.section.length > 0 &&
        pathSection.meta) {
        return pathSection.meta.tag ?? UNTAGGED_TAG;
    }
    else {
        return UNDEFINED_TAG;
    }
};
/**
 * Maps the given spline section to a path
 * @param splineSection the spline section to map
 * @returns a Path representation of the spline section
 */
const splineSectionToPath = (splineSection) => {
    const path = [];
    let tag = undefined;
    let taggedSection = [];
    let meta;
    splineSection.forEach((curve) => {
        if ((curve.tag ?? UNTAGGED_TAG) != tag) {
            taggedSection = [];
            tag = curve.tag ?? UNTAGGED_TAG;
            meta = {
                tStart: CubicBezier.getStart(curve).x,
                tEnd: CubicBezier.getStart(curve).x,
                tag: curve.tag,
            };
            path.push({ section: taggedSection, meta });
        }
        taggedSection.push(curve);
        meta.tEnd = CubicBezier.getEnd(curve).x;
    });
    return path;
};
/**
 * Resets all tags in the spline (in place) to undefined
 * @param spline  The spline to reset
 */
export const untagSpline = (spline) => {
    if (spline) {
        spline.forEach((section) => section.forEach((curve) => (curve.tag = undefined)));
    }
};
/**
 * Split the path at given thresholds and tag each split with the optional
 * tags. This is also called 'binning'.
 * @param spline The spline to convert to a path.
 * @param thresholds The optional values where to split the spline and optionally tag the splits
 * @returns The (optionally splitted)  path
 */
export const splineToPath = (spline, thresholds) => {
    let path = [];
    if (spline && spline.length > 0) {
        //find the start and end times of the spline
        const xStart = CubicBezier.getStart(spline[0][0]).x;
        const lastBezierCurve = spline[spline.length - 1][spline[spline.length - 1].length - 1];
        const xEnd = CubicBezier.getEnd(lastBezierCurve).x;
        const thresholdValues = thresholds
            ? thresholds.map((threshold) => threshold.value)
            : [];
        const thresholdTags = thresholds
            ? thresholds.map((threshold) => threshold.tag)
            : [];
        let x = xStart;
        //iterate over the spline
        spline.forEach((splineSection) => {
            //test if current section does not adjoin the last added PathSection / end of the path, i.e. test for a gap
            if (CubicBezier.getStart(splineSection[0]).x > x) {
                //insert an 'undefined path section because we have a gap at our hands
                path.push({
                    section: [], //holds no sections because it is a gap
                    meta: {
                        tStart: x, //the gap starts at the end of the path
                        tEnd: CubicBezier.getStart(splineSection[0]).x, //and ends at the start of current spline section
                        tag: undefined, //and is undefined
                    },
                });
            }
            //convert current spline section to path sections and append to the path
            const splittedSplineSection = thresholds != undefined && thresholds.length > 0
                ? splitSplineSectionAtThresholds(splineSection, thresholdValues, thresholdTags)
                : splineSection;
            const splittedPathSection = splineSectionToPath(splittedSplineSection);
            path = path.concat(splittedPathSection);
            //advance t to the end of the path
            x = path[path.length - 1].meta.tEnd;
        });
        if (x < xEnd) {
            //append an 'undefined' PathSection if we stopped before the end of the spline
            path.push({
                section: [],
                meta: { tStart: x, tEnd: xEnd, tag: undefined },
            });
        }
        return path;
    }
    else {
        return [];
    }
};
const splitSplineSectionAtThresholds = (splineSection, thresholds, tags) => {
    if (thresholds) {
        thresholds.forEach((threshold, i) => {
            splitSplineSectionAtThreshold(splineSection, threshold, tags[i]);
        });
    }
    return splineSection;
};
const splitSplineSectionAtThreshold = (splineSection, threshold, tag) => {
    let i = 0;
    while (i < splineSection.length) {
        const curve = splineSection[i];
        const splittedCurve = curve.tag
            ? [curve]
            : splitBezier(curve, threshold, tag);
        if (i > 0) {
            //the endpoints might be slightly adjusted
            //when they match the threshold. Here we make sure
            //that the end of the preceeding one matches the 
            // start of the following one.
            splineSection[i - 1].cp[3].x = splittedCurve[0].cp[0].x;
            splineSection[i - 1].cp[3].y = splittedCurve[0].cp[0].y;
        }
        splineSection.splice(i, 1, ...splittedCurve);
        i += splittedCurve.length;
    }
    return splineSection;
};
/**
 * Split the path at sections and tag each split with the optional
 * tag. This is also called 'binning'.
 * @param spline The spline to convert to a path.
 * @param tagSections The optional sections to apply tags to
 * @returns The (optionally splitted)  path
 */
export const splineToTaggedPath = (spline, tagSections) => {
    let splittedSpline = [];
    //construct an array of tagSections sorted by their start time
    const sortedTagSections = (tagSections ? tagSections.concat() : []).sort((t0, t1) => t0.start - t1.start);
    if (spline && spline.length > 0) {
        //find the start and end times of the spline
        const tStart = CubicBezier.getStart(spline[0][0]).x;
        const lastBezierCurve = spline[spline.length - 1][spline[spline.length - 1].length - 1];
        const tEnd = CubicBezier.getEnd(lastBezierCurve).x;
        let t = tStart;
        //iterate over the spline
        spline.forEach((splineSection) => {
            //test if current section does not adjoin the last added PathSection / end of the path, i.e. test for a gap
            if (CubicBezier.getStart(splineSection[0]).x > t) {
                //insert an 'undefined path section because we have a gap at our hands
                splittedSpline.push({
                    section: [], //holds no sections because it is a gap
                    meta: {
                        tStart: t, //the gap starts at the end of the path
                        tEnd: CubicBezier.getStart(splineSection[0]).x, //and ends at the start of current spline section
                        tag: undefined, //and is undefined
                    },
                });
            }
            //convert current spline section to path sections and append to the path
            splittedSpline = splittedSpline.concat(splineSectionToPath(splitSplineSectionAtTagSections(splineSection, sortedTagSections)));
            //advance t to the end of the path
            t = splittedSpline[splittedSpline.length - 1].meta.tEnd;
        });
        if (t < tEnd) {
            //append an 'undefined' PathSection if we stopped before the end of the spline
            splittedSpline.push({
                section: [],
                meta: { tStart: t, tEnd, tag: undefined },
            });
        }
        return splittedSpline;
    }
    else {
        return [];
    }
};
const splitSplineSectionAtTagSections = (splineSection, tagSections) => {
    if (splineSection.length > 0) {
        const lastCurve = splineSection.at(-1);
        const end = CubicBezier.getEnd(lastCurve).x;
        //remove all tag sections that lie before current splineSection
        while (tagSections.length > 0 &&
            tagSections[0].end <= CubicBezier.getStart(splineSection[0]).x)
            tagSections.shift();
        if (tagSections.length > 0 && tagSections[0].start < end) {
            splitSplineSectionAtTagSection(splineSection, tagSections);
        }
    }
    return splineSection;
};
const splitSplineSectionAtTagSection = (splineSection, tagSections) => {
    let i = 0;
    while (i < splineSection.length && tagSections.length > 0) {
        //skip all curves that lie in front of the first tag section
        while (i < splineSection.length &&
            CubicBezier.getEnd(splineSection[i]).x <= tagSections[0].start)
            i++;
        if (i < splineSection.length) {
            //current curve overlaps with current tagSection(i.e the first tag section) -> split the curve
            const splittedCurve = splitCurveAtTagSection(splineSection[i], tagSections[0]);
            //replace the curve with the splits
            splineSection.splice(i, 1, ...splittedCurve);
            if (splittedCurve.length > 1) {
                //advance i to the last split
                i += splittedCurve.length - 1;
            }
            else {
                //unless there was no split, then just advance to the next curve
                i++;
            }
            //advance to the next tag section by removing the first tag section if current curve starts after tag section
            if (i < splineSection.length &&
                CubicBezier.getStart(splineSection[i]).x >= tagSections[0].end) {
                tagSections.shift();
            }
        }
    }
    return splineSection;
};
const splitCurveAtTagSection = (curve, tagSection) => {
    //this function assumes (partly) overlap between curve and tag section
    let t;
    t = CubicBezier.tForX(curve, tagSection.start);
    const curveAlignsAtStart = CubicBezier.hasTAtStart(t);
    t = CubicBezier.tForX(curve, tagSection.end);
    const curveAlignsAtEnd = CubicBezier.hasTAtEnd(t);
    if (curveAlignsAtStart || CubicBezier.getStart(curve).x >= tagSection.start) {
        //curve starts in tag section
        if (curveAlignsAtEnd || CubicBezier.getEnd(curve).x <= tagSection.end) {
            //curve lies in the range of the tag section
            curve.tag = tagSection.tag;
            return [curve];
        }
        else {
            //curve ends later then tag section
            const splittedCurve = CubicBezier.splitAtX(curve, tagSection.end);
            splittedCurve[0].tag = tagSection.tag;
            if (splittedCurve.length > 1) {
                splittedCurve[1].tag = curve.tag;
            }
            else {
                //curve ends later, but still very very close, so no split after all
            }
            return splittedCurve;
        }
    }
    else {
        //curve start before tag section
        if (CubicBezier.getEnd(curve).x <= tagSection.end) {
            //curve ends in tag section
            const splittedCurve = CubicBezier.splitAtX(curve, tagSection.start);
            splittedCurve[0].tag = curve.tag;
            if (splittedCurve.length > 1) {
                splittedCurve[1].tag = tagSection.tag;
            }
            else {
                //curve ends in tag section, but still very very close to the end, so no split after all
            }
            return splittedCurve;
        }
        else {
            //tag section lies on curve
            //split at section start
            const splittedCurve = CubicBezier.splitAtX(curve, tagSection.start);
            splittedCurve[0].tag = curve.tag;
            splittedCurve[1].tag = tagSection.tag;
            //split at section end
            splittedCurve.splice(1, 1, ...CubicBezier.splitAtX(splittedCurve[1], tagSection.end));
            if (splittedCurve.length == 3) {
                splittedCurve[2].tag = curve.tag;
            }
            else {
                console.log("Expected 3 splits");
            }
            return splittedCurve;
        }
    }
};
